import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import type { AccentTone } from '../dashboard/dashboard.js';
import styles from './records.module.css';

/**
 * THE RECORD-SCREEN ANATOMY, converted from the Stitch markup.
 *
 * WHERE THIS CAME FROM. Nine reference files — Screens 02, 03, 04, 05, 06, 07,
 * 31, 32 and 33 of `01_Tenant_App/Finance` — read as markup rather than as
 * pictures. Each component below names the Stitch node it is, and the CSS module
 * beside it names the Tailwind class every rule came from. The screens they
 * replace were assembled out of the generic patterns (a `Card`, a `CardHeader`,
 * a `Table`), which is a different screen that happens to carry the same data.
 *
 * WHY IT IS NOT CALLED FINANCE ANYTHING. The reference draws three things every
 * module has: a list of records with a summary band over it, one record's own
 * page, and a document on its way out of the building. Finance is the first
 * module with all three, so the anatomy lands here.
 *
 * NOTHING HERE COMPUTES A FACT. Values arrive formatted. The one thing that
 * looks like arithmetic is `fraction` on a meter or a distribution part, which
 * is a bar width — drawing, not accounting — and the caller passes it already
 * divided from two exact server integers.
 *
 * NOTHING HERE DECIDES WHAT A CALLER MAY SEE. Every action is a prop; a screen
 * that is given no `onSend` renders no send button, and the route guard and the
 * server refuse the same request again either way.
 */

const TONE_CLASS: Record<AccentTone, string> = {
  teal: styles.toneTeal ?? '',
  success: styles.toneSuccess ?? '',
  info: styles.toneInfo ?? '',
  warning: styles.toneWarning ?? '',
  danger: styles.toneDanger ?? '',
  neutral: styles.toneNeutral ?? '',
  accent: styles.toneAccent ?? '',
};

function cx(...parts: readonly (string | false | undefined)[]): string {
  return parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' ');
}

/* =========================================================================
   THE SUMMARY BAND — the figures a reader takes in before the rows
   ========================================================================= */

/**
 * `grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md`, or Screen 07's
 * `lg:grid-cols-5`.
 *
 * THE COUNT IS THE REFERENCE'S, NOT THE DATA'S. A band told to draw four cards
 * draws four in one desktop row; five where Stitch draws five ageing buckets.
 * Nothing may add a card because a server grew a metric — that is how a row
 * designed for four ends up two rows tall.
 */
export function SummaryBand({
  columns = 4,
  children,
}: {
  readonly columns?: 4 | 5;
  readonly children: ReactNode;
}) {
  return (
    <div className={cx(styles.summaryBand, columns === 5 && styles.summaryBand_five)}>
      {children}
    </div>
  );
}

export interface SummaryCardProps {
  readonly label: string;
  /** Formatted by the caller. A card that formatted money would be guessing. */
  readonly value?: string | undefined;
  readonly icon?: IconName | undefined;
  readonly tone?: AccentTone | undefined;
  /** The reference's left rail, drawn on the one card that is a warning. */
  readonly urgent?: boolean | undefined;
  /** `inline-flex … rounded bg-primary-fixed` beside the caption. */
  readonly badge?: ReactNode;
  readonly caption?: ReactNode;
  /** Six or so points; drawn as the reference's 64×20 trend line. */
  readonly points?: readonly number[] | undefined;
  /** 0–1, already divided from two exact server integers. A bar, not a figure. */
  readonly fraction?: number | undefined;
  /** Why there is no figure. A refused or unset value is never a zero. */
  readonly unavailable?: string | undefined;
}

/**
 * `bg-surface-container-lowest p-space-md rounded-xl shadow-sm flex flex-col
 *  justify-between relative overflow-hidden`.
 *
 * Head is `flex items-start justify-between` — label left, a soft 36px badge
 * right. Foot is `flex items-center justify-between mt-space-md` — a chip, a
 * caption and the trend line.
 */
export function SummaryCard({
  label,
  value,
  icon,
  tone = 'teal',
  urgent = false,
  badge,
  caption,
  points,
  fraction,
  unavailable,
}: SummaryCardProps) {
  const toneClass = TONE_CLASS[urgent ? 'danger' : tone];
  return (
    <div className={cx(styles.summaryCard, urgent && styles.summaryCard_railed, toneClass)}>
      {urgent && <span className={styles.summaryRail} aria-hidden="true" />}
      <div className={styles.summaryHead}>
        <span className={cx(styles.summaryLabel, urgent && styles.summaryLabel_urgent)}>
          {label}
        </span>
        {icon !== undefined && (
          <span className={styles.summaryIcon} aria-hidden="true">
            <Icon name={icon} size={18} />
          </span>
        )}
      </div>
      {unavailable === undefined ? (
        /*
         * THE FIGURE CARRIES ITS OWN NAME. Two cards on a screen can hold the
         * same number — three attempts and three invoices — and a reader with a
         * screen reader, or a test, needs to be able to ask for one of them.
         */
        <span
          className={cx(styles.summaryValue, urgent && styles.summaryValue_urgent)}
          aria-label={`${label} value`}
        >
          {value ?? '—'}
        </span>
      ) : (
        <span className={styles.summaryUnavailable}>{unavailable}</span>
      )}
      {fraction !== undefined && (
        <span className={styles.summaryMeter} aria-hidden="true">
          <span
            className={styles.summaryMeterFill}
            style={{ width: `${String(Math.max(0, Math.min(100, fraction * 100)))}%` }}
          />
        </span>
      )}
      {(badge !== undefined || caption !== undefined || points !== undefined) && (
        <div className={styles.summaryFoot}>
          {badge}
          {caption !== undefined && (
            <span className={cx(styles.summaryCaption, urgent && styles.summaryCaption_urgent)}>
              {caption}
            </span>
          )}
          {points !== undefined && points.length > 1 && <Trend points={points} />}
        </div>
      )}
    </div>
  );
}

/** `inline-flex items-center gap-1 px-space-xs py-[2px] rounded` */
export function SummaryBadge({
  tone = 'teal',
  icon,
  children,
}: {
  readonly tone?: AccentTone;
  readonly icon?: IconName | undefined;
  readonly children: ReactNode;
}) {
  return (
    <span className={cx(styles.summaryBadge, TONE_CLASS[tone])}>
      {icon !== undefined && <Icon name={icon} size={11} />}
      {children}
    </span>
  );
}

/**
 * `svg.w-16 h-5` with one `path` — the reference's inline trend line.
 *
 * The points are the server's own series; the only arithmetic is the map onto a
 * 64×20 viewBox, which is what drawing a line means.
 */
function Trend({ points }: { readonly points: readonly number[] }) {
  const highest = Math.max(...points);
  const lowest = Math.min(...points);
  const span = highest - lowest || 1;
  const step = 60 / (points.length - 1);
  const path = points
    .map((point, index) => {
      const x = 2 + index * step;
      const y = 17 - ((point - lowest) / span) * 14;
      return `${index === 0 ? 'M' : 'L'}${String(Math.round(x * 10) / 10)} ${String(Math.round(y * 10) / 10)}`;
    })
    .join(' ');
  return (
    <svg className={styles.summarySpark} viewBox="0 0 64 20" fill="none" aria-hidden="true">
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* =========================================================================
   THE FILTER BAR — Screen 02's search, selects and view controls
   ========================================================================= */

export function FilterBar({
  flush = false,
  children,
}: {
  /** Inside a records card, where Screens 04, 07 and 33 put the same bar. */
  readonly flush?: boolean;
  readonly children: ReactNode;
}) {
  return <div className={cx(styles.filterBar, flush && styles.filterBar_flush)}>{children}</div>;
}

/** `relative flex-1 max-w-xl` with a leading `search` glyph. */
export function FilterSearch({
  label,
  value,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className={styles.filterSearch}>
      <span className={styles.filterSearchIcon} aria-hidden="true">
        <Icon name="magnifying-glass" size={15} />
      </span>
      <input
        className={styles.filterSearchInput}
        type="search"
        aria-label={label}
        value={value}
        placeholder={placeholder ?? label}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

export function FilterControls({ children }: { readonly children: ReactNode }) {
  return <div className={styles.filterControls}>{children}</div>;
}

/** `flex items-center bg-surface-container-low rounded px-2 h-[36px]` */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className={styles.filterField}>
      <span className={styles.filterFieldLabel}>{label}</span>
      <select
        className={styles.filterFieldSelect}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** `flex items-center bg-surface-container-lowest px-3 py-1.5 rounded-lg` — a
 *  filter field showing a value the caller cannot change here. */
export function FilterFact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <span className={styles.filterField}>
      <span className={styles.filterFieldLabel}>{label}</span>
      <span className={styles.filterFieldSelect}>{children}</span>
    </span>
  );
}

export function FilterNote({ children }: { readonly children: ReactNode }) {
  return <span className={styles.filterNote}>{children}</span>;
}

/* =========================================================================
   THE RECORDS CARD — the workspace every list screen is built around
   ========================================================================= */

export function RecordsCard({ children }: { readonly children: ReactNode }) {
  return <div className={styles.recordsCard}>{children}</div>;
}

/** `p-space-md flex items-center justify-between bg-surface-container-low` */
export function RecordsToolbar({
  icon,
  title,
  count,
  actions,
  children,
}: {
  readonly icon?: IconName | undefined;
  readonly title: string;
  /** The reference's `3 Items` pill. Already worded by the caller. */
  readonly count?: string | undefined;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <div className={styles.recordsToolbar}>
      <div className={styles.recordsToolbarTitles}>
        {icon !== undefined && (
          <span className={styles.recordsTitleIcon} aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
        )}
        <h2 className={styles.recordsTitle}>{title}</h2>
        {count !== undefined && <span className={styles.recordsCount}>{count}</span>}
        {children}
      </div>
      {actions !== undefined && <div className={styles.recordsToolbarActions}>{actions}</div>}
    </div>
  );
}

/** `overflow-x-auto w-full`, carrying the reference's own table density. */
export function RecordsScroll({
  dense = false,
  wrapNames = false,
  children,
}: {
  /** Six-pixel gutters and the secondary figure size, for a table that must fit
   *  seven columns in the 958 pixels a 1280-wide window leaves. */
  readonly dense?: boolean;
  /** Let a party's name wrap rather than hold its column open. Screen 07's
   *  pivot needs it; a customer list reads worse with it. */
  readonly wrapNames?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div
      className={cx(
        styles.recordsScroll,
        dense && styles.recordsScroll_dense,
        wrapNames && styles.recordsScroll_wrapNames,
      )}
    >
      {children}
    </div>
  );
}

/**
 * `px-space-md py-2.5 bg-surface-container-low flex items-center
 *  justify-between` — how many rows this is, and what else is true of them.
 */
export function RecordsFooter({ children }: { readonly children: ReactNode }) {
  return <div className={styles.recordsFooter}>{children}</div>;
}

export function RecordsCount({ children }: { readonly children: ReactNode }) {
  return <strong className={styles.recordsFooterStrong}>{children}</strong>;
}

/* =========================================================================
   THE TAB STRIP — Screen 04's status tabs, Screen 03's record tabs
   ========================================================================= */

export interface RecordTab {
  readonly id: string;
  readonly label: string;
  /** The reference's count pill. A tab with no count shows none. */
  readonly count?: number | undefined;
}

/**
 * A VIEW OVER ROWS THE SERVER ALREADY SENT, and never a second filter.
 *
 * The counts are of the same rows and the "all" tab hides nothing: choosing a
 * tab narrows what is DRAWN, exactly as sorting a column does. Which rows exist
 * at all is the record predicate's answer and was decided in SQL.
 */
export function TabStrip({
  label,
  tabs,
  active,
  onSelect,
}: {
  readonly label: string;
  readonly tabs: readonly RecordTab[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <div className={styles.tabStrip} role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={cx(styles.tab, tab.id === active && styles.tab_active)}
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          <span>{tab.label}</span>
          {tab.count !== undefined && <span className={styles.tabCount}>{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* =========================================================================
   THE SPLIT — the 12-column grid every record and document screen uses
   ========================================================================= */

export function Split({ children }: { readonly children: ReactNode }) {
  return <div className={styles.split}>{children}</div>;
}

/** `lg:col-span-7` (Screens 05, 32) or `lg:col-span-8` (Screens 03, 06, 31, 33). */
export function SplitMain({
  span = 7,
  children,
}: {
  readonly span?: 7 | 8;
  readonly children: ReactNode;
}) {
  return (
    <div className={cx(span === 8 ? styles.splitMain_8 : styles.splitMain_7, styles.stack)}>
      {children}
    </div>
  );
}

/** `lg:col-span-5` or `lg:col-span-4`, the partner of the same row. */
export function SplitSide({
  span = 5,
  children,
}: {
  readonly span?: 5 | 4;
  readonly children: ReactNode;
}) {
  return (
    <div className={cx(span === 4 ? styles.splitSide_4 : styles.splitSide_5, styles.stack)}>
      {children}
    </div>
  );
}

/** `space-y-gutter-desktop` — a column of panels. */
export function Stack({ children }: { readonly children: ReactNode }) {
  return <div className={styles.stack}>{children}</div>;
}

/* =========================================================================
   THE PANEL — `p-space-lg rounded-xl shadow-sm space-y-space-md`
   ========================================================================= */

export function Panel({
  variant,
  children,
}: {
  /** `inset` is the reference's `bg-surface-container-low` module inside a
   *  panel; `flush` is a panel whose own content draws the padding. */
  readonly variant?: 'inset' | 'flush' | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section
      className={cx(
        styles.panel,
        variant === 'inset' && styles.panel_inset,
        variant === 'flush' && styles.panel_flush,
      )}
    >
      {children}
    </section>
  );
}

/**
 * `flex items-center justify-between pb-space-xs` — an uppercase label on the
 * left and, on the right, the one thing this panel offers.
 */
export function PanelHead({
  label,
  title,
  icon,
  children,
}: {
  /** The reference's `label-sm uppercase tracking-wider` caption. */
  readonly label?: string | undefined;
  /** A real heading, where Stitch draws `headline-sm` instead of a label. */
  readonly title?: string | undefined;
  readonly icon?: IconName | undefined;
  readonly children?: ReactNode;
}) {
  return (
    <div className={styles.panelHead}>
      {title === undefined ? (
        <span className={styles.panelLabel}>
          {icon !== undefined && (
            <span className={styles.panelLabelIcon} aria-hidden="true">
              <Icon name={icon} size={14} />
            </span>
          )}
          {label}
        </span>
      ) : (
        <h2 className={styles.panelTitle}>
          {icon !== undefined && (
            <span className={styles.panelLabelIcon} aria-hidden="true">
              <Icon name={icon} size={15} />
            </span>
          )}
          {title}
        </h2>
      )}
      {children}
    </div>
  );
}

/** `font-label-sm text-primary cursor-pointer hover:underline` */
export function PanelAction({
  icon,
  onClick,
  children,
}: {
  readonly icon?: IconName | undefined;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button type="button" className={styles.panelAction} onClick={onClick}>
      {icon !== undefined && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

/** `px-space-xs py-[2px] rounded bg-surface-container-high` */
export function PanelTag({ children }: { readonly children: ReactNode }) {
  return <span className={styles.panelTag}>{children}</span>;
}

export function PanelFoot({ children }: { readonly children: ReactNode }) {
  return <div className={styles.panelFoot}>{children}</div>;
}

/** `flex items-center gap-1 text-secondary text-[12px]` */
export function PanelNote({
  icon,
  children,
}: {
  readonly icon?: IconName | undefined;
  readonly children: ReactNode;
}) {
  return (
    <span className={styles.panelNote}>
      {icon !== undefined && (
        <span className={styles.panelNoteIcon} aria-hidden="true">
          <Icon name={icon} size={13} />
        </span>
      )}
      {children}
    </span>
  );
}

/* ---- the field grid inside a panel --------------------------------------- */

export function FieldGrid({
  columns = 3,
  children,
}: {
  readonly columns?: 2 | 3 | 4;
  readonly children: ReactNode;
}) {
  const column =
    columns === 2 ? styles.fieldGrid_2 : columns === 4 ? styles.fieldGrid_4 : styles.fieldGrid_3;
  return <div className={cx(styles.fieldGrid, column)}>{children}</div>;
}

export function Field({
  label,
  span2 = false,
  children,
}: {
  readonly label: string;
  /** `col-span-2`, as Screen 05 gives the currency row. */
  readonly span2?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className={cx(styles.field, span2 && styles.fieldSpan2)}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </div>
  );
}

export function FieldValue({
  strong = false,
  muted = false,
  children,
}: {
  readonly strong?: boolean;
  readonly muted?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <span
      className={cx(
        styles.fieldValue,
        strong && styles.fieldValue_strong,
        muted && styles.fieldValue_muted,
      )}
    >
      {children}
    </span>
  );
}

/**
 * `h-9 px-space-sm bg-surface-container-low rounded flex items-center
 *  justify-between` with a trailing `lock` glyph.
 *
 * A VALUE DRAWN IN THE SHAPE OF ITS CONTROL, because the reference draws the
 * invoice number as a field and it is not one: nobody types an invoice number.
 * A disabled `<input>` would be a control that refuses; this is a fact.
 */
export function FieldLocked({
  icon = 'lock-simple',
  children,
}: {
  readonly icon?: IconName;
  readonly children: ReactNode;
}) {
  return (
    <span className={styles.fieldLocked}>
      <span>{children}</span>
      <span className={styles.fieldLockedIcon} aria-hidden="true">
        <Icon name={icon} size={14} />
      </span>
    </span>
  );
}

/** `w-full h-9 px-space-sm bg-surface-container-low rounded` */
export function FieldInput({
  label,
  value,
  type = 'text',
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly type?: 'text' | 'date' | 'email' | 'number';
  readonly placeholder?: string | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <input
      className={styles.fieldInput}
      type={type}
      aria-label={label}
      value={value}
      {...(placeholder === undefined ? {} : { placeholder })}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/** `w-full p-space-sm rounded bg-surface-container-low` */
export function FieldTextarea({
  label,
  value,
  rows = 3,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly rows?: number;
  readonly placeholder?: string | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <textarea
      className={styles.fieldTextarea}
      aria-label={label}
      rows={rows}
      value={value}
      {...(placeholder === undefined ? {} : { placeholder })}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

/**
 * `flex items-center bg-surface-container-low rounded px-space-sm h-10
 *  gap-space-sm` around a borderless `<select>`, as Screen 05 draws the
 * customer chooser.
 */
export function SelectShell({
  label,
  value,
  icon,
  options,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly icon?: IconName | undefined;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly placeholder?: string | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className={styles.selectShell}>
      {icon !== undefined && (
        <span className={styles.selectShellIcon} aria-hidden="true">
          <Icon name={icon} size={16} />
        </span>
      )}
      <select
        className={styles.selectShellInput}
        aria-label={label}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.selectShellIcon} aria-hidden="true">
        <Icon name="caret-down" size={14} />
      </span>
    </div>
  );
}

/** `w-5 h-5 rounded bg-primary-container text-on-primary font-bold` */
export function CurrencyMark({ children }: { readonly children: ReactNode }) {
  return (
    <span className={styles.currencyMark} aria-hidden="true">
      {children}
    </span>
  );
}

/* =========================================================================
   THE IDENTITY HEADER — which record this is and what state it is in
   ========================================================================= */

/**
 * `bg-surface-container-lowest rounded-xl p-space-lg shadow-sm flex ...
 *  justify-between gap-space-md`, WITHOUT the reference's `<h1>`.
 *
 * The shell names the page from the route table. This names the RECORD — a
 * different fact, and the one a reader needs on a page reached from a list.
 */
export function IdentityCard({
  mark,
  tone = 'teal',
  name,
  code,
  chips,
  meta,
  actions,
}: {
  /** Initials, as the reference's `w-10 h-10 rounded-lg` tile draws them. */
  readonly mark?: string | undefined;
  readonly tone?: AccentTone;
  readonly name: string;
  readonly code?: string | undefined;
  readonly chips?: ReactNode;
  readonly meta?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <div className={cx(styles.identityCard, TONE_CLASS[tone])}>
      <div className={styles.identityBody}>
        {mark !== undefined && (
          <span className={styles.identityMark} aria-hidden="true">
            {mark}
          </span>
        )}
        <div className={styles.identityText}>
          <div className={styles.identityTitleRow}>
            <h2 className={styles.identityName}>{name}</h2>
            {code !== undefined && <span className={styles.identityCode}>{code}</span>}
            {chips}
          </div>
          {meta !== undefined && <div className={styles.identityMeta}>{meta}</div>}
        </div>
      </div>
      {actions !== undefined && <div className={styles.identityActions}>{actions}</div>}
    </div>
  );
}

export function MetaStrong({ children }: { readonly children: ReactNode }) {
  return <span className={styles.identityMetaStrong}>{children}</span>;
}

export function MetaSeparator() {
  return (
    <span className={styles.identityMetaSep} aria-hidden="true">
      •
    </span>
  );
}

/* =========================================================================
   THE LIFECYCLE STEPPER — Screen 06
   ========================================================================= */

export interface LifecycleStep {
  readonly label: string;
  /** The reference prints a date under each reached step; `undefined` is one
   *  that has not happened, and it says so rather than showing a guess. */
  readonly when?: string | undefined;
  readonly state: 'done' | 'current' | 'pending';
}

/**
 * `relative flex items-center justify-between` over a 2px rule whose filled
 * part reaches the last step that has actually happened.
 */
export function Lifecycle({ steps }: { readonly steps: readonly LifecycleStep[] }) {
  const reached = steps.reduce((last, step, index) => (step.state === 'pending' ? last : index), 0);
  const percent = steps.length < 2 ? 0 : (reached / (steps.length - 1)) * 100;
  return (
    <div className={styles.lifecycle}>
      <span className={styles.lifecycleTrack} aria-hidden="true">
        <span className={styles.lifecycleTrackFill} style={{ width: `${String(percent)}%` }} />
      </span>
      {steps.map((step) => (
        <div key={step.label} className={styles.lifecycleStep}>
          <span
            className={cx(
              styles.lifecycleDot,
              step.state === 'done' && styles.lifecycleDot_done,
              step.state === 'current' && styles.lifecycleDot_current,
            )}
            aria-hidden="true"
          >
            <Icon name={step.state === 'done' ? 'check' : 'calendar-blank'} size={13} />
          </span>
          <span
            className={cx(
              styles.lifecycleLabel,
              step.state === 'pending' && styles.lifecycleLabel_pending,
            )}
          >
            {step.label}
          </span>
          {/*
            A STEP THAT HAS HAPPENED AND CARRIES NO DATE SAYS NOTHING, rather
            than "Not yet" under a tick. A draft is created the moment the
            record exists and nothing stores when; contradicting the tick is
            worse than leaving the line blank.
          */}
          <span className={styles.lifecycleWhen}>
            {step.when ?? (step.state === 'pending' ? 'Not yet' : '')}
          </span>
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
   THE TOTALS PANEL — Screen 05's calculation, Screen 06's document totals
   ========================================================================= */

export function Totals({ children }: { readonly children: ReactNode }) {
  return <div className={styles.totals}>{children}</div>;
}

/** `flex justify-between items-center pt-space-xs` over a divide rule. */
export function TotalsRow({
  label,
  sublabel,
  amount,
  emphasis = false,
  credit = false,
}: {
  readonly label: string;
  readonly sublabel?: string | undefined;
  /** Formatted by the caller — the server's figure, not one computed here. */
  readonly amount: string;
  readonly emphasis?: boolean;
  readonly credit?: boolean;
}) {
  return (
    <div
      className={cx(
        styles.totalsRow,
        emphasis && styles.totalsRow_emphasis,
        credit && styles.totalsRow_credit,
      )}
    >
      <span className={styles.totalsLabel}>
        <span>{label}</span>
        {sublabel !== undefined && <span className={styles.totalsSubLabel}>{sublabel}</span>}
      </span>
      <span className={styles.totalsAmount}>{amount}</span>
    </div>
  );
}

/** `bg-surface-container-low p-space-md rounded-lg flex items-center
 *  justify-between` — the grand total, and what is due when. */
export function TotalsBanner({
  label,
  amount,
  side,
}: {
  readonly label: string;
  readonly amount: string;
  readonly side?: ReactNode;
}) {
  return (
    <div className={styles.totalsBanner}>
      <span className={styles.totalsBannerText}>
        <span className={styles.totalsBannerLabel}>{label}</span>
        <span className={styles.totalsBannerValue}>{amount}</span>
      </span>
      {side !== undefined && <span className={styles.totalsBannerSide}>{side}</span>}
    </div>
  );
}

/* =========================================================================
   THE DISTRIBUTION STRIP — Screen 07
   ========================================================================= */

export interface DistributionPart {
  readonly id: string;
  readonly label: string;
  /** 0–1, divided from two exact server integers. A width, not a figure. */
  readonly fraction: number;
  readonly tone: AccentTone;
  /** What the part is, in the caller's own words, for the hover title. */
  readonly title: string;
}

/** `w-full h-4 rounded-lg overflow-hidden flex p-0.5 gap-0.5` */
export function Distribution({ parts }: { readonly parts: readonly DistributionPart[] }) {
  return (
    <div className={styles.distribution}>
      {parts
        .filter((part) => part.fraction > 0)
        .map((part) => (
          <span
            key={part.id}
            className={cx(styles.distributionPart, TONE_CLASS[part.tone])}
            style={{ width: `${String(Math.max(0, Math.min(100, part.fraction * 100)))}%` }}
            title={part.title}
          />
        ))}
    </div>
  );
}

/** `flex items-center gap-4 text-label-sm` */
export function Legend({ children }: { readonly children: ReactNode }) {
  return <div className={styles.legend}>{children}</div>;
}

export function LegendItem({
  tone = 'teal',
  children,
}: {
  readonly tone?: AccentTone;
  readonly children: ReactNode;
}) {
  return (
    <span className={cx(styles.legendItem, TONE_CLASS[tone])}>
      <span className={styles.legendSwatch} aria-hidden="true" />
      {children}
    </span>
  );
}

/* =========================================================================
   THE TIMELINE — Screens 06 and 33
   ========================================================================= */

export function Timeline({ children }: { readonly children: ReactNode }) {
  return <ol className={styles.timeline}>{children}</ol>;
}

/**
 * `relative flex flex-col gap-0.5` with a ringed dot on the rule.
 *
 * EVERY EVENT IS ONE THE SYSTEM ACTUALLY RECORDED. The reference draws read
 * receipts, portal opens and payment-link activations; nothing here observes
 * any of those, so a caller has none to pass (§4E).
 */
export function TimelineEvent({
  tone = 'neutral',
  when,
  source,
  title,
  children,
}: {
  readonly tone?: AccentTone;
  readonly when: string;
  readonly source?: string | undefined;
  readonly title: string;
  readonly children?: ReactNode;
}) {
  return (
    <li className={cx(styles.timelineEvent, TONE_CLASS[tone])}>
      <span className={styles.timelineDot} aria-hidden="true" />
      <span className={styles.timelineWhen}>
        <span>{when}</span>
        {source !== undefined && <span className={styles.timelineSource}>{source}</span>}
      </span>
      <p className={styles.timelineTitle}>{title}</p>
      {children !== undefined && <p className={styles.timelineDetail}>{children}</p>}
    </li>
  );
}

/* =========================================================================
   THE DOCUMENT STAGE — Screen 31
   ========================================================================= */

/** `flex flex-wrap items-center justify-between px-space-md py-space-sm
 *  rounded-xl shadow-sm` — the viewer's own controls. */
export function ViewerBar({ children }: { readonly children: ReactNode }) {
  return <div className={styles.viewerBar}>{children}</div>;
}

export function ViewerGroup({ children }: { readonly children: ReactNode }) {
  return <span className={styles.viewerGroup}>{children}</span>;
}

/**
 * `bg-surface-container-low rounded-xl p-space-lg flex justify-center
 *  items-start overflow-x-auto` around `w-full max-w-[780px] bg-white`.
 *
 * THE SHEET IS THE SERVER'S OWN PDF, framed at the reference's paper width. The
 * reference redraws an invoice in HTML; doing that here would put a second
 * renderer in the product and the two would disagree the first time one changed.
 */
export function DocumentStage({ src, title }: { readonly src: string; readonly title: string }) {
  return (
    <div className={styles.documentStage}>
      <iframe className={styles.documentSheet} src={src} title={title} />
    </div>
  );
}

/* =========================================================================
   THE CHANNEL ROW AND THE ATTACHMENT — Screens 05, 31 and 32
   ========================================================================= */

/** `p-space-md rounded-lg bg-surface-container-low` */
export function Channel({
  title,
  chip,
  icon,
  tone = 'teal',
  detail,
  children,
}: {
  readonly title: string;
  readonly chip?: ReactNode;
  readonly icon?: IconName | undefined;
  readonly tone?: AccentTone;
  readonly detail?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <div className={cx(styles.channel, TONE_CLASS[tone])}>
      <div className={styles.channelBody}>
        <div className={styles.channelTitleRow}>
          <span className={styles.channelTitle}>{title}</span>
          {chip}
        </div>
        {detail !== undefined && <p className={styles.channelDetail}>{detail}</p>}
        {children}
      </div>
      {icon !== undefined && (
        <span className={styles.channelMark} aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
      )}
    </div>
  );
}

/** `flex items-center justify-between p-space-sm bg-surface-container-low
 *  rounded` — a file, its size and its digest. */
export function Attachment({
  name,
  meta,
  icon = 'tray',
  action,
}: {
  readonly name: string;
  readonly meta: string;
  readonly icon?: IconName;
  readonly action?: ReactNode;
}) {
  return (
    <div className={styles.attachment}>
      <span className={styles.attachmentBody}>
        <span className={styles.attachmentIcon} aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
        <span className={styles.attachmentText}>
          <span className={styles.attachmentName}>{name}</span>
          <span className={styles.attachmentMeta}>{meta}</span>
        </span>
      </span>
      {action}
    </div>
  );
}

/* =========================================================================
   THE MAIL PREVIEW — Screen 32's right column
   ========================================================================= */

export function MailEnvelope({ children }: { readonly children: ReactNode }) {
  return <div className={styles.mailEnvelope}>{children}</div>;
}

export function MailField({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <span className={styles.mailField}>
      <span className={styles.mailFieldLabel}>{label}</span>
      <span className={styles.mailFieldValue}>{children}</span>
    </span>
  );
}

export function MailBody({ children }: { readonly children: ReactNode }) {
  return <div className={styles.mailBody}>{children}</div>;
}

export function MailBrand({ name, note }: { readonly name: string; readonly note: string }) {
  return (
    <div className={styles.mailBrand}>
      <span className={styles.mailBrandName}>{name}</span>
      <span className={styles.mailBrandNote}>{note}</span>
    </div>
  );
}

export function MailText({
  muted = false,
  children,
}: {
  readonly muted?: boolean;
  readonly children: ReactNode;
}) {
  return <p className={cx(styles.mailText, muted && styles.mailText_muted)}>{children}</p>;
}

/** `p-space-md rounded-lg bg-surface-container-low space-y-space-sm` */
export function MailSummary({
  label,
  chip,
  total,
  split,
  foot,
}: {
  readonly label: string;
  readonly chip?: ReactNode;
  readonly total: string;
  readonly split: string;
  readonly foot?: ReactNode;
}) {
  return (
    <div className={styles.mailSummary}>
      <div className={styles.mailSummaryHead}>
        <span className={styles.totalsBannerLabel}>{label}</span>
        {chip}
      </div>
      <div className={styles.mailSummaryFigures}>
        <span className={styles.mailSummaryTotal}>{total}</span>
        <span className={styles.mailSummarySplit}>{split}</span>
      </div>
      {foot !== undefined && <div className={styles.mailSummarySplit}>{foot}</div>}
    </div>
  );
}

export function MailFooter({ children }: { readonly children: ReactNode }) {
  return <div className={styles.mailFooter}>{children}</div>;
}

/* =========================================================================
   SMALL SHARED PARTS used inside table cells
   ========================================================================= */

/**
 * `w-7 h-7 rounded bg-surface-container-high` + name over code.
 *
 * THE INITIALS ARE THE NAME'S OWN, not an avatar. The reference puts a
 * photograph here; there are no photographs in this product and a stock face
 * beside a real debtor would be the clearest possible lie on the screen.
 */
export function EntityCell({
  mark,
  name,
  sub,
  onOpen,
}: {
  readonly mark?: string | undefined;
  readonly name: string;
  readonly sub?: string | undefined;
  /** Absent, the name is plain text rather than a link that goes nowhere. */
  readonly onOpen?: (() => void) | undefined;
}) {
  return (
    <span className={styles.entity}>
      {mark !== undefined && (
        <span className={styles.entityMark} aria-hidden="true">
          {mark}
        </span>
      )}
      <span className={styles.entityText}>
        {onOpen === undefined ? (
          <span className={styles.entityName}>{name}</span>
        ) : (
          <button type="button" className={styles.entityLink} onClick={onOpen}>
            {name}
          </button>
        )}
        {sub !== undefined && <span className={styles.entitySub}>{sub}</span>}
      </span>
    </span>
  );
}

/** `px-space-xs py-1 rounded bg-surface-container-low font-numeric-table` */
export function CodeChip({ children }: { readonly children: ReactNode }) {
  return <span className={styles.codeChip}>{children}</span>;
}

/** `font-body-sm text-[12px] text-secondary mt-0.5` — a row's second line. */
export function RowDetail({ children }: { readonly children: ReactNode }) {
  return <p className={styles.rowDetail}>{children}</p>;
}

/** `input.w-full bg-transparent focus:outline-none` inside a grid cell. */
export function CellInput({
  label,
  value,
  numeric = false,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly numeric?: boolean;
  readonly placeholder?: string | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <input
      className={cx(styles.cellInput, numeric && styles.cellInput_numeric)}
      type="text"
      inputMode={numeric ? 'decimal' : 'text'}
      aria-label={label}
      value={value}
      {...(placeholder === undefined ? {} : { placeholder })}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

export function CellSelect({
  label,
  value,
  options,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly placeholder?: string | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <select
      className={styles.cellSelect}
      aria-label={label}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** `p-space-sm bg-surface-container-lowest flex items-center justify-between` */
export function Ribbon({
  note,
  children,
}: {
  readonly note?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div className={styles.ribbon}>
      <div className={styles.ribbonActions}>{children}</div>
      {note !== undefined && <span className={styles.ribbonNote}>{note}</span>}
    </div>
  );
}

export function RowActions({ children }: { readonly children: ReactNode }) {
  return <span className={styles.rowActions}>{children}</span>;
}

/** `p-1 text-secondary hover:text-on-surface rounded` */
export function RowAction({
  icon,
  label,
  danger = false,
  disabled = false,
  onClick,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cx(styles.rowAction, danger && styles.rowAction_danger)}
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}
